import React, { Component } from 'react';
import { buildPath } from '../utils/route_utils.js';
import { updateSEO } from '../utils/seo_utils';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AnnouncementBanner from '../components/AnnouncementBanner';
import Breadcrumb from '../components/Breadcrumb';
import '../css/LaundryBlogPost.css';
import '../css/LaundryLineBlogPost.css';

const FIG_STAGES = '/image/laundry_line_blog_fig1.png';
const FIG_DAGGER = '/image/laundry_line_blog_fig2.png';
const FIG_HEADS = '/image/laundry_line_blog_fig3.png';
const VIDEO_SRC = '/video/laundry_line_blog_video.mp4';
const VIDEO_POSTER = '/image/laundry_line_blog_video_poster.jpg';
const OG_IMAGE = 'https://www.nexuni.com/image/laundry_line_blog_og.jpg';
const CANONICAL_ROUTE = '/blog/laundry-line-gr00t';
const NVIDIA_GROOT_URL = 'https://developer.nvidia.com/isaac/gr00t';

const SEO_TITLE = 'Automating a Laundry Factory Line with NVIDIA Isaac GR00T 1.7 | Nexuni';
const SEO_DESC =
  'How Nexuni took a post-trained NVIDIA Isaac GR00T 1.7 policy from trained to running autonomously on a laundry factory line: a DAgger workflow that collects corrections from the policy’s own failures, and a lightweight completion head on the frozen GR00T backbone.';

const TAGS = ['Robotics', 'PhysicalAI', 'NVIDIA', 'IsaacGR00T', 'VLA', 'LaundryAutomation', 'TextileIndustry'];

/**
 * Technical blog article: GR00T 1.7 towel handling on a laundry factory line
 * (DAgger corrections + completion head). Shares the editorial styling of
 * LaundryBlogPost.css; LaundryLineBlogPost.css adds the technical-article
 * pieces (wide diagrams, portrait video, results table, list, tags).
 * Content is intentionally English-only across all locales (zh / en / ja);
 * only the surrounding chrome (Navbar, Breadcrumb, Footer) is localized.
 */
class LaundryLineBlogPost extends Component {
  constructor(props) {
    super(props);
    this.state = {
      lang: this.props.lang,
      visible: {}
    };
    this.update_lang = this.update_lang.bind(this);
    this.navigate = this.navigate.bind(this);
    this.observerCallback = this.observerCallback.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.sectionRefs = [];
    this.progressRef = React.createRef();
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.setSEO();
    this.observer = new IntersectionObserver(this.observerCallback, {
      threshold: 0.14,
      rootMargin: '0px 0px -60px 0px'
    });
    this.sectionRefs.forEach(ref => ref && this.observer.observe(ref));
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  }

  componentWillUnmount() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('scroll', this.onScroll);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.lang !== this.state.lang) {
      this.setState({ lang: nextProps.lang });
    }
  }

  setSEO() {
    // Shared head updates (lang, title, description, robots, canonical,
    // og/twitter text, hreflang). The article is English in every locale.
    const same = text => ({ zh: text, en: text, ja: text });
    updateSEO('blog', this.state.lang, CANONICAL_ROUTE, {
      title: same(SEO_TITLE),
      description: same(SEO_DESC)
    });

    const setMeta = (name, content, attr = 'name') => {
      let el = document.querySelector('meta[' + attr + '="' + name + '"]');
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('og:image', OG_IMAGE, 'property');
    setMeta('og:type', 'article', 'property');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:image', OG_IMAGE);
  }

  onScroll() {
    const el = this.progressRef.current;
    if (!el) return;
    const doc = document.documentElement;
    const scrolled = doc.scrollTop || document.body.scrollTop;
    const height = doc.scrollHeight - doc.clientHeight || 1;
    el.style.width = Math.min(100, Math.max(0, (scrolled / height) * 100)) + '%';
  }

  observerCallback(entries) {
    const updates = {};
    entries.forEach(entry => {
      if (entry.isIntersecting) updates[entry.target.dataset.reveal] = true;
    });
    if (Object.keys(updates).length > 0) {
      this.setState(prev => ({ visible: { ...prev.visible, ...updates } }));
    }
  }

  registerRef(el, id) {
    if (el && !this.sectionRefs.includes(el)) {
      el.dataset.reveal = id;
      this.sectionRefs.push(el);
      if (this.observer) this.observer.observe(el);
    }
  }

  // Props for a scroll-revealed block: <p {...this.reveal('id')}>
  reveal(id, className) {
    return {
      className: (className ? className + ' ' : '') + 'lbp_reveal' + (this.state.visible[id] ? ' lbp_in' : ''),
      ref: el => this.registerRef(el, id)
    };
  }

  update_lang(newLang) {
    this.props.updateLang(newLang);
  }

  navigate(route) {
    window.location.href = buildPath(this.state.lang, route);
  }

  renderDiagram(id, src, alt, label, caption) {
    return (
      <figure {...this.reveal(id, 'lbp_figure ltb_figure_wide')}>
        <a className="ltb_diagram" href={src} target="_blank" rel="noopener noreferrer" aria-label={label + ' (open full size)'}>
          <img src={src} alt={alt} loading="lazy" />
        </a>
        <figcaption>
          <span className="lbp_cap_tick" />
          <span><strong>{label}</strong> {caption}</span>
        </figcaption>
      </figure>
    );
  }

  render() {
    const lang = this.state.lang;

    return (
      <div className={'lbp_page ltb_page ' + lang}>
        <div className="lbp_progress" aria-hidden="true">
          <span ref={this.progressRef} className="lbp_progress_bar" />
        </div>

        <AnnouncementBanner lang={lang} />
        <Navbar lang={lang} updateLangFunc={this.update_lang} />
        <Breadcrumb
          lang={lang}
          items={[
            { label: { zh: '部落格', en: 'Blog', ja: 'ブログ' }, route: '/blog' },
            { label: { zh: '洗衣產線自動化', en: 'Laundry Line Automation', ja: 'ランドリーラインの自動化' } }
          ]}
        />

        {/* ===== Hero ===== */}
        <header className="lbp_hero">
          <div className="lbp_hero_grid" aria-hidden="true" />
          <div className="lbp_hero_glow" aria-hidden="true" />
          <div className="lbp_hero_inner">
            <a className="lbp_back" href={buildPath(lang, '/blog')}>&larr; Back to Blog</a>
            <h1 className="lbp_title">
              Automating a Laundry Factory Line with NVIDIA Isaac GR00T 1.7
            </h1>
            <div className="lbp_meta">
              <span className="lbp_meta_brand">By Nexuni</span>
              <span className="lbp_meta_dot" />
              <span>October 13, 2026</span>
              <span className="lbp_meta_dot" />
              <span>6 min read</span>
            </div>
          </div>
        </header>

        {/* ===== Article ===== */}
        <article className="lbp_article">
          <p {...this.reveal('lede', 'lbp_lede')}>
            Vision-language-action (VLA) models can learn dexterous manipulation from teleoperated demonstrations, but a
            policy that has learned a task is not yet a policy that can run unattended on a production line. Two gaps
            remain: the policy must recover from the failure states it creates for itself, and it must signal reliably
            when the task is finished so that downstream equipment can take over.
          </p>
          <p {...this.reveal('intro')}>
            At Nexuni, we are building autonomous robots for a{' '}
            <a href={buildPath(lang, '/blog/laundry-gr00t')}>large laundry factory in Singapore</a>, where drying,
            industrial ironing, and garment folding still depend on manual labor and the goal is continuous 24/7
            operation. We post-trained{' '}
            <a href={NVIDIA_GROOT_URL} target="_blank" rel="noopener noreferrer">NVIDIA Isaac GR00T 1.7</a>, an open
            VLA foundation model for generalist robots, on a bimanual towel-handling task and deployed it on hardware
            that runs the task end to end and hands off to a conveyor. This post covers the two techniques that took
            the policy from trained to running autonomously in the real world: a DAgger workflow that collects
            corrections from the policy&rsquo;s own failures, and a lightweight completion head on the frozen GR00T
            backbone.
          </p>

          {/* ----- Section 1 ----- */}
          <section className="lbp_section">
            <h2 {...this.reveal('h_task', 'lbp_h2')}><span className="lbp_h2_mark" />Real-world towel manipulation with GR00T 1.7</h2>
            <p {...this.reveal('task1')}>
              The task is towel handling on the factory line (Figure 1). The robot picks up a towel with the right hand,
              lifts it over the worktable, and releases it. It then grasps both sides with both hands and pulls backward
              to spread the towel, grasps the two exposed corners, lifts, and places the towel on a conveyor belt.
            </p>
          </section>

          {this.renderDiagram(
            'fig1',
            FIG_STAGES,
            'Five-stage diagram of the towel-handling task: pick up, lift and release, spread, lift by corners, and hand off to the conveyor.',
            'Figure 1.',
            'The five stages of the bimanual towel-handling task, from the input bin to the conveyor handoff'
          )}

          <p {...this.reveal('task2')}>
            Towel handling is a useful stress test for a VLA model. The object is deformable, it has no canonical pose,
            and every episode begins from a different crumple, so the policy cannot succeed by replaying a trajectory. We
            post-trained GR00T 1.7 using three cameras and a bimanual end-effector action space.
          </p>

          {/* ----- Section 2 ----- */}
          <section className="lbp_section">
            <h2 {...this.reveal('h_dagger', 'lbp_h2')}><span className="lbp_h2_mark" />Learning from the policy&rsquo;s own failures with DAgger</h2>
            <p {...this.reveal('dagger1')}>
              Teleoperated demonstrations only cover the states a skilled operator visits, so they contain almost none
              of the failure states the policy produces on its own, and collecting more of them does not close that
              gap. DAgger (dataset aggregation) lets the policy learn from the failure states it actually produces. The
              policy first executes the task autonomously on the real robot. When it enters a clearly problematic
              state, a human operator takes over through teleoperation and demonstrates the recovery. That correction
              is recorded and added to the training data for the next round of post-training (Figure 2).
            </p>
          </section>

          {this.renderDiagram(
            'fig2',
            FIG_DAGGER,
            'DAgger loop: post-train GR00T 1.7, autonomous rollout on the real robot, operator intervention, and teleoperated corrections aggregated into the dataset for the next round.',
            'Figure 2.',
            'DAgger loop for post-training GR00T 1.7. Green icons mark automated stages and blue icons mark operator-in-the-loop stages'
          )}

          <p {...this.reveal('dagger2')}>
            This matters particularly for deformable-object manipulation. The shape of a towel depends on the entire
            history of contact that preceded it, so a failure state arranged by hand rarely reproduces the states a
            policy rollout actually generates. The most valuable correction data comes from the failure states the
            policy reaches on its own.
          </p>
          <p {...this.reveal('dagger3')}>
            The workflow also changed how we collect data. Rather than adding more complete demonstrations, we focus on
            the failure modes the policy exhibits repeatedly and collect targeted corrections around those states. Each
            round therefore concentrates new data on the policy&rsquo;s current weaknesses instead of adding more
            examples of cases it already handles.
          </p>

          {/* ----- Section 3 ----- */}
          <section className="lbp_section">
            <h2 {...this.reveal('h_done', 'lbp_h2')}><span className="lbp_h2_mark" />Teaching the policy when the task is complete</h2>
            <p {...this.reveal('done1')}>
              Action prediction alone is not enough for automation. The system also needs a reliable indication that
              the task is complete, so that it can hand off to the conveyor belt without a human watching.
            </p>
            <p {...this.reveal('done2')}>
              We first evaluated rule-based approaches using robot pose, visual heuristics, and event counts.
              Intermediate and terminal states in this task are often visually and kinematically similar: the spreading
              motion and the final lift both hold the towel suspended between two grippers at a similar height, which
              made fixed rules unreliable in real rollouts. Event-based rules had a related problem. Because the policy
              retries after a failed grasp, the number of lifts before completion is not fixed. We therefore treated
              task completion as a learned classification problem.
            </p>
            <p {...this.reveal('done3')}>
              We added a lightweight auxiliary completion head on top of the frozen GR00T representation (Figure 3).
              The action head remains unchanged, while the completion head predicts a binary completion signal from the
              same shared features. In practice this is a small head of roughly 2M parameters that attends over the
              backbone&rsquo;s image tokens alongside the state vector, evaluated in the same forward pass the action
              head already runs.
            </p>
          </section>

          {this.renderDiagram(
            'fig3',
            FIG_HEADS,
            'Architecture: camera images, language instruction, and robot state feed the frozen NVIDIA Isaac GR00T 1.7 backbone, whose shared features drive both the unchanged action head and a trainable ~2M-parameter completion head.',
            'Figure 3.',
            'GR00T 1.7 with an auxiliary completion head on the frozen backbone. Both heads read the same features in one forward pass'
          )}

          <p {...this.reveal('props_intro')}>Three properties made this the right structure:</p>
          <ul {...this.reveal('props', 'ltb_list')}>
            <li>
              <strong>The action policy is untouched.</strong> A policy server running with the completion head
              produces identical actions to one running without it; it simply returns one additional output. The
              action model and the completion signal can be developed independently.
            </li>
            <li>
              <strong>Inference cost is one small attention block,</strong> not a second backbone pass.
            </li>
            <li>
              <strong>Training takes minutes, not days.</strong> With the backbone frozen, the features are constant
              and can be cached, which turns the completion signal into something we can iterate on quickly rather
              than a training-run-sized commitment.
            </li>
          </ul>

          {/* ----- Section 4 ----- */}
          <section className="lbp_section">
            <h2 {...this.reveal('h_results', 'lbp_h2')}><span className="lbp_h2_mark" />Results</h2>
            <p {...this.reveal('res1')}>
              <strong>Completion head.</strong> We evaluated the completion head on 114 held-out episodes, 65
              completed and 49 not. Timing error is measured against the labeled completion frame (Table 1).
            </p>
          </section>

          <figure {...this.reveal('table1', 'lbp_figure ltb_table_fig')}>
            <div className="ltb_table_wrap">
              <table className="ltb_table">
                <thead>
                  <tr>
                    <th scope="col">Metric</th>
                    <th scope="col">Result</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Completed episodes triggered</td>
                    <td className="ltb_table_val">64 of 65</td>
                  </tr>
                  <tr>
                    <td>False triggers on incomplete episodes</td>
                    <td className="ltb_table_val">0 of 49</td>
                  </tr>
                  <tr>
                    <td>Median trigger timing error</td>
                    <td className="ltb_table_val">&minus;0.05 s</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <figcaption>
              <span className="lbp_cap_tick" />
              <span><strong>Table 1.</strong> Completion-head performance on 114 held-out episodes</span>
            </figcaption>
          </figure>

          <p {...this.reveal('res2')}>
            The head still has one characterized failure mode on the real robot: it can fire when the towel has been
            lifted but not fully spread, a state that is visually similar to completion but semantically different.
          </p>
          <p {...this.reveal('res3')}>
            <strong>Full-task behavior.</strong> Video 1 shows the deployed system running on the real robot. The
            policy takes a towel from the input bin, places it on the work surface, spreads it with both hands, and
            lifts it by the exposed corners. The completion signal then triggers the handoff and the belt carries the
            towel away. The footage is continuous and unedited apart from being played at 5x speed, and it covers two
            consecutive cycles with no human intervention between them.
          </p>

          <figure {...this.reveal('video1', 'lbp_figure lbp_figure_inline ltb_video_fig')}>
            <div className="lbp_video_frame">
              <video controls playsInline preload="metadata" poster={VIDEO_POSTER}>
                <source src={VIDEO_SRC} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            <figcaption>
              <span className="lbp_cap_tick" />
              <span>
                <strong>Video 1.</strong> Two consecutive towel-handling cycles at 5x speed. Every conveyor handoff is
                triggered by the completion head
              </span>
            </figcaption>
          </figure>

          <p {...this.reveal('res4')}>
            Every transfer to the belt in the clip is triggered by the completion head rather than by a timer or a
            fixed motion script. That is the difference between a policy that performs a task and a policy that can be
            integrated into a production line.
          </p>

          {/* ----- Section 5 ----- */}
          <section className="lbp_section">
            <h2 {...this.reveal('h_summary', 'lbp_h2')}><span className="lbp_h2_mark" />Summary</h2>
            <p {...this.reveal('sum1')}>
              Real-world policy improvement is not only a model-training problem. It is a continuous iteration across
              data collection, on-robot rollout, and system integration.
            </p>
            <p {...this.reveal('sum2')}>
              Two practices made the difference on the laundry factory line. First, make each new batch of data respond
              directly to the problems the policy exposes during real execution, rather than simply pursuing more data.
              Second, give the policy a way to recognize when a stage is complete, so that it integrates with the
              surrounding automation equipment and finishes the task more efficiently.
            </p>
          </section>

          <ul {...this.reveal('tags', 'ltb_tags')} aria-label="Tags">
            {TAGS.map(tag => (
              <li key={tag} className="ltb_tag">#{tag}</li>
            ))}
          </ul>
        </article>

        <Footer lang={lang} navigate={this.navigate} />
      </div>
    );
  }
}

export default LaundryLineBlogPost;
